import { useEffect, useState } from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'

// ─── Tool registry ───────────────────────────────────────────
// Every gated tool on the dashboard. Access is granted per-tool via Cognito
// groups (`tool:<id>`), or wholesale via the `admin` group. The owner email
// is a bootstrap fallback so the account that owns the AWS stack is never
// locked out before groups exist in the user pool.

export const TOOL_IDS = [
  'watch-monitor',
  'financial-app',
  'family-finance',
  'fantasy-football',
  'family-planner',
  'nventures',
] as const
export type ToolId = (typeof TOOL_IDS)[number]

export const TOOL_LABELS: Record<ToolId, string> = {
  'watch-monitor': 'Watch Monitor',
  'financial-app': 'Securities Analytics',
  'family-finance': 'Finances',
  'fantasy-football': 'Fantasy Football',
  'family-planner': 'Family Planner',
  nventures: 'NVentures',
}

const OWNER_EMAIL = (import.meta.env.VITE_OWNER_EMAIL || 'nathanaelbrown@gmail.com').toLowerCase()

export const ADMIN_GROUP = 'admin'
export const toolGroup = (tool: ToolId) => `tool:${tool}`

export interface AccessProfile {
  loading: boolean
  email: string
  name: string
  groups: string[]
  isAdmin: boolean
  tools: ToolId[]
}

export const EMPTY_ACCESS: AccessProfile = {
  loading: true,
  email: '',
  name: '',
  groups: [],
  isAdmin: false,
  tools: [],
}

function resolveAccess(email: string, name: string, groups: string[]): AccessProfile {
  const isAdmin = groups.includes(ADMIN_GROUP) || email.toLowerCase() === OWNER_EMAIL
  const tools = isAdmin
    ? [...TOOL_IDS]
    : TOOL_IDS.filter((id) => groups.includes(toolGroup(id)))
  return { loading: false, email, name, groups, isAdmin, tools }
}

export async function fetchAccessProfile(): Promise<AccessProfile> {
  // Mirrors the dev-only auth bypass in App.tsx; dead code in production builds.
  if (import.meta.env.DEV && import.meta.env.VITE_DEV_BYPASS_AUTH === 'true') {
    return resolveAccess('nathanaelbrown@gmail.com', 'Nathanael Brown (dev)', [ADMIN_GROUP])
  }
  try {
    const session = await fetchAuthSession()
    const payload = session.tokens?.idToken?.payload
    if (!payload) return { ...EMPTY_ACCESS, loading: false }
    const email = typeof payload.email === 'string' ? payload.email : ''
    const name = typeof payload.name === 'string' ? payload.name : ''
    const rawGroups = payload['cognito:groups']
    const groups = Array.isArray(rawGroups) ? rawGroups.map(String) : []
    return resolveAccess(email, name, groups)
  } catch {
    return { ...EMPTY_ACCESS, loading: false }
  }
}

export function canAccessTool(profile: AccessProfile, tool: ToolId): boolean {
  return profile.isAdmin || profile.tools.includes(tool)
}

export function useAccess(): AccessProfile {
  const [profile, setProfile] = useState<AccessProfile>(EMPTY_ACCESS)

  useEffect(() => {
    let cancelled = false
    fetchAccessProfile().then((p) => {
      if (!cancelled) setProfile(p)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return profile
}
